import { baseApi, defaultQueryFreshness } from "./baseApi";

type JsonValue = Record<string, unknown> | unknown[];

export const disputesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createDispute: builder.mutation<unknown, Record<string, unknown>>({
      query: (disputeData) => ({ url: "/disputes", method: "POST", data: disputeData }),
      invalidatesTags: [{ type: "Disputes", id: "LIST" }],
    }),
    getBuyerDisputes: builder.query<unknown, string>({
      query: (buyerId) => ({ url: `/disputes/buyer/${buyerId}`, method: "GET" }),
      providesTags: (_res, _err, buyerId) => [{ type: "Disputes", id: `buyer:${buyerId}` }],
      ...defaultQueryFreshness,
    }),
    getSellerDisputes: builder.query<unknown, string>({
      query: (sellerId) => ({ url: `/disputes/seller/${sellerId}`, method: "GET" }),
      providesTags: (_res, _err, sellerId) => [{ type: "Disputes", id: `seller:${sellerId}` }],
      ...defaultQueryFreshness,
    }),
    getDisputeById: builder.query<unknown, string>({
      query: (disputeId) => ({ url: `/disputes/${disputeId}`, method: "GET" }),
      providesTags: (_res, _err, disputeId) => [{ type: "Disputes", id: String(disputeId) }],
      ...defaultQueryFreshness,
    }),
    respondToDispute: builder.mutation<unknown, { disputeId: string; response: string; evidence?: JsonValue[] }>({
      query: ({ disputeId, response, evidence = [] }) => ({
        url: `/disputes/${disputeId}/respond`,
        method: "PATCH",
        data: { response, evidence },
      }),
      invalidatesTags: (_res, _err, { disputeId }) => [{ type: "Disputes", id: String(disputeId) }, { type: "Disputes", id: "LIST" }],
    }),
    resolveDispute: builder.mutation<unknown, { disputeId: string; resolution: string; refundAmount: number; adminNotes: string }>({
      query: ({ disputeId, resolution, refundAmount, adminNotes }) => ({
        url: `/disputes/${disputeId}/resolve`,
        method: "PATCH",
        data: { resolution, refundAmount, adminNotes },
      }),
      invalidatesTags: (_res, _err, { disputeId }) => [{ type: "Disputes", id: String(disputeId) }, { type: "Disputes", id: "LIST" }],
    }),
    markDisputeInReview: builder.mutation<unknown, { disputeId: string; adminNotes?: string }>({
      query: ({ disputeId, adminNotes = "" }) => ({
        url: `/disputes/${disputeId}/review`,
        method: "PATCH",
        data: { adminNotes },
      }),
      invalidatesTags: (_res, _err, { disputeId }) => [{ type: "Disputes", id: String(disputeId) }, { type: "Disputes", id: "LIST" }],
    }),
    getAllDisputes: builder.query<unknown, void>({
      query: () => ({ url: "/disputes", method: "GET" }),
      providesTags: [{ type: "Disputes", id: "LIST" }],
      ...defaultQueryFreshness,
    }),
  }),
});

export const {
  useCreateDisputeMutation,
  useGetBuyerDisputesQuery,
  useGetSellerDisputesQuery,
  useGetDisputeByIdQuery,
  useRespondToDisputeMutation,
  useResolveDisputeMutation,
  useMarkDisputeInReviewMutation,
  useGetAllDisputesQuery,
} = disputesApi;
